import * as React from 'react';
import PhotosPickerGroup, {
  PhotosPickerGroupBaseProps,
  PhotosPickerGroupsObj,
} from '../../../../shared/components/PhotosPickerGroup';
import connectWithRouter from '../../../../shared/components/redux/connectWithRouter';
import {
  getEbayAndVariationUrls,
  getEbayAndVariationUrlsList,
  getPhotosUnion,
  getSelectedVariationSelector,
  getUpdatedEbayListingValues,
  getUpdatedFieldValue,
  getVariationsPictureData,
} from '../ebayListingSelectors';
import {FIELD_NAMES, MAX_PHOTOS_ALLOWED, SHOW_MODES, USE_DEFAULT_PHOTOS, VARIATIONS_SELECTED_ASPECT} from '../consts';
import * as _ from 'lodash';
import {updateDescriptionPhotos} from '../../listingsHelper';
import update from 'immutability-helper';
import ChildrenFader from '../../../../shared/components/ChildrenFader';
import AbstractListerField from './AbstractListerField';
import {createStyles, WithStyles, withStyles} from '@material-ui/core/styles';
import {Theme} from '@material-ui/core/styles/createMuiTheme';
import Toggle from 'material-ui/Toggle';

interface ListerPhotosPickerGroupProps extends PhotosPickerGroupBaseProps {
  variations: PhotosPickerGroupsObj;
  aspect: string;
  onChange: (event: object) => void;
  site: string;
  showMode: string;
  ebayGalleryUrls: string[];
  ebayWithVariationsUrls: string[];
  descriptionGalleryUrls: string[];
  id?: string;
}

interface ListerPhotosPickerGroupState {
  hideVariations: boolean;
  lastValidAspect: string;
}

@connectWithRouter(state => {
  return {
    site: _.get(getUpdatedEbayListingValues(state), FIELD_NAMES.EBAY_SITE),
    variations: getVariationsPictureData(state),
    aspect: getSelectedVariationSelector(state),
    descriptionGalleryUrls: getUpdatedFieldValue(state, FIELD_NAMES.CL_PICTURE_URLS) || [],
    ebayGalleryUrls: _.get(getUpdatedEbayListingValues(state), FIELD_NAMES.PICTURE_URL) || [],
    ebayWithVariationsUrls: getEbayAndVariationUrls(state) || [],
  };
})
class ListerPhotosPickerGroup extends React.PureComponent<
  ListerPhotosPickerGroupProps & WithStyles<typeof styles>,
  ListerPhotosPickerGroupState
> {
  constructor(props) {
    super(props);
    const {aspect, variations} = this.props;
    const useDefaultPhotos = aspect === USE_DEFAULT_PHOTOS;
    this.state = {
      hideVariations: useDefaultPhotos,
      lastValidAspect: useDefaultPhotos ? _.keys(variations)[0] : aspect,
    };
  }

  updateDescriptionPhotosOnChange = (newVariations, selectedAspect, variationName, reorder, deletion) => {
    const {descriptionGalleryUrls, ebayGalleryUrls, showMode, variations} = this.props;
    if (deletion) {
      const deletion = _.difference(
        variations[selectedAspect][variationName],
        newVariations[selectedAspect][variationName]
      );
      return updateDescriptionPhotos(showMode, descriptionGalleryUrls, reorder, deletion, null);
    } else {
      return updateDescriptionPhotos(
        showMode,
        descriptionGalleryUrls,
        reorder,
        null,
        getEbayAndVariationUrlsList(ebayGalleryUrls, newVariations[selectedAspect])
      );
    }
  };

  updateDescriptionPhotosOnAspectChange = newVariationsUrls => {
    const {variations, descriptionGalleryUrls, ebayGalleryUrls, showMode, aspect} = this.props;
    if (showMode === SHOW_MODES.SHOW_ALL || showMode === SHOW_MODES.SHOW_SOME) {
      const prevVariationsUrls = _.flatMap(variations[aspect]);
      return getPhotosUnion(
        getEbayAndVariationUrlsList(ebayGalleryUrls, newVariationsUrls),
        _.difference(descriptionGalleryUrls, prevVariationsUrls)
      );
    }
    return descriptionGalleryUrls;
  };

  handleChange = (value, selectedAspect, modifiedVariationName = null, reorder = false, deletion = false) => {
    const {onChange} = this.props;
    value = update(value, {
      [VARIATIONS_SELECTED_ASPECT]: {
        $set: selectedAspect,
      },
    });
    let newDescriptionGalleryUrls = [];
    if (!_.isEmpty(modifiedVariationName)) {
      newDescriptionGalleryUrls = this.updateDescriptionPhotosOnChange(
        value,
        selectedAspect,
        modifiedVariationName,
        reorder,
        deletion
      );
    } else {
      // aspect selection changed
      newDescriptionGalleryUrls = this.updateDescriptionPhotosOnAspectChange(value[selectedAspect]); // value indicates new variations
      if (selectedAspect !== USE_DEFAULT_PHOTOS) {
        this.setState({lastValidAspect: selectedAspect});
      } // remember aspect for toggle
    }
    value = {
      [FIELD_NAMES.VARIATIONS_PICTURES]: value,
      [FIELD_NAMES.CL_PICTURE_URLS]: newDescriptionGalleryUrls,
    };
    onChange(value);
  };

  handleToggle = (event, value) => {
    const {variations} = this.props;
    const {hideVariations, lastValidAspect} = this.state;
    this.setState({hideVariations: !hideVariations});
    this.handleChange(variations, value ? USE_DEFAULT_PHOTOS : lastValidAspect);
  };

  render() {
    const {
      classes: {switchPosition},
      variations,
      aspect,
      id,
      ...other
    } = this.props;
    const {hideVariations} = this.state;
    const visibleAspects = _.without(_.keys(variations), USE_DEFAULT_PHOTOS);
    return (
      <div>
        <div>
          <Toggle
            id={`${id}-use-default-photos`}
            labelPosition="left"
            label={USE_DEFAULT_PHOTOS}
            onToggle={this.handleToggle}
            checked={hideVariations}
            labelStyle={{whiteSpace: 'nowrap'}}
            style={{width: 'auto'}}
            className={switchPosition}
            {...other}
          />
        </div>
        {!hideVariations ? (
          <ChildrenFader>
            <PhotosPickerGroup
              id={id}
              picturesURLsGroup={variations}
              currentGroup={aspect}
              updatePhotosGroup={this.handleChange}
              maxPhotosAllowed={MAX_PHOTOS_ALLOWED}
              groupPickerText="Assign images to variation:"
              groupsNames={visibleAspects}
              {...other}
            />
          </ChildrenFader>
        ) : (
          undefined
        )}
      </div>
    );
  }
}

const styles = (theme: Theme) =>
  createStyles({
    switchPosition: {
      paddingLeft: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      paddingTop: theme.spacing(2),
    },
  });

const ListerPhotosPickerGroupWithStyles = withStyles(styles)(ListerPhotosPickerGroup);

_.assign(ListerPhotosPickerGroupWithStyles.contextTypes, AbstractListerField.contextTypes);

export default ListerPhotosPickerGroupWithStyles;
